import {
  DataGridPremium,
  DataGridPremiumProps,
  GridCallbackDetails,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridInitialState,
  GridPinnedColumnFields,
  GridRowGroupingModel,
  GridRowId,
  GridRowParams,
  GridSortModel,
  MuiEvent,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { GridStatePremium } from '@mui/x-data-grid-premium/models/gridStatePremium'
import { QueryKey } from '@tanstack/react-query'
import clsx from 'clsx'
import isArray from 'lodash/isArray'
import isNil from 'lodash/isNil'
import { usePathname } from 'next/navigation'
import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { trySaveToLocalStorage } from '@omnea-app/fe-browser-utils/trySaveToLocalStorage'
import { omneaDataGridClasses } from '@omnea-app/omui/dataGridClasses'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { useSyncDataSourceOnInvalidate } from '../ControlledTable/hooks/useSyncDataSourceOnInvalidate'
import { CustomPagination } from './CustomPagination'
import { DataGridContainer } from './DataGridContainer'
import { DefaultNoRowsState } from './DefaultNoRowsState'
import { useDataGridAnalytics } from './hooks/useDataGridAnalytics'
import { gridShowsSkeleton } from './hooks/useGridShowsSkeleton'
import { useHasScrolledHorizontally } from './hooks/useHasScrolledHorizontally'
import { useMuiDataGridLocaleText } from './hooks/useMuiDataGridLocaleText'
import { usePreserveScrollPosition } from './hooks/usePreserveScrollPosition'
import { LinkableGridRow, RowLinkContext, type RowClickableProp, type RowVariantProp } from './LinkableGridRow'
import { useEditingRowsContext } from './RowEditingContext'
import { ROW_SELECTION_CHECKBOX_COLUMN, RowSelectionStatusContext, type RowSelectionStatusProp } from './RowSelection'
import { isGroupRow } from './utils/isGroupRow'
import { mergeColumnOrder } from './utils/mergeColumnOrder'
import { pinGroupingColumnFirst } from './utils/pinGroupingColumnFirst'
import { skipValueGetterOnAutogeneratedRows } from './utils/skipValueGetterOnAutogeneratedRows'
import { stripPersistedGroupingLeafFold } from './utils/stripPersistedGroupingLeafFold'

export type TableEditMode = 'row' | 'cell'

const NO_PINNED_COLUMNS: GridPinnedColumnFields = { left: [], right: [] }

const LOADING_OVERLAY_SLOT_PROPS: NonNullable<DataGridPremiumProps['slotProps']>['loadingOverlay'] = {
  noRowsVariant: 'skeleton',
}

// getRowClassName is withheld: row styling goes through getRowVariant so the omui theme stays the only
// thing that decides what a variant looks like.
export interface DataGridBaseProps
  extends Omit<DataGridPremiumProps, 'getRowClassName'>, RowVariantProp, RowClickableProp, RowSelectionStatusProp {
  stateKey?: string | null
  defaultSortModel?: {
    field: string
    sort: 'asc' | 'desc'
  }
  initialColumnVisibility?: GridColumnVisibilityModel
  // Must be the pre-localStorage-merge default, not the current model, or the column-visibility analytics
  // diffs are computed against the wrong baseline.
  defaultColumnVisibilityModel?: GridColumnVisibilityModel
  getDefaultRowLink?: (id: GridRowId) => string | null
  allowPageScroll?: boolean
  maxHeight?: string
  pinnedColumns?: {
    left?: string[]
    right?: string[]
  }
  editMode?: TableEditMode
  TableEmptyState?: () => ReactNode
  onRowClick?: (params: GridRowParams, event: MuiEvent<React.MouseEvent<HTMLElement>>, details: GridCallbackDetails) => void
  pageSizeOptions?: number[]
  hasExternalHeader?: boolean
  totalCountLoading?: boolean
  totalCountIsApprox?: boolean
  // Opt a data-source table into server-side row grouping; off by default so other tables stay flat.
  enableServerSideRowGrouping?: boolean
  // Drops restored grouping fields the current columns can't serve, applied to the persisted model at
  // seed. Opt-in (default no-op) so only a table whose data source can't serve every grouping needs it.
  sanitizeRestoredGrouping?: (model: GridRowGroupingModel) => GridRowGroupingModel
  // For grids whose group rows span every column: a pinned cell would escape the span. Needs controlled
  // pinnedColumns, which MUI re-applies on ungroup; the user's pins are persisted in place of the emptied ones.
  unpinColumnsWhileGrouped?: boolean
  // Reports whether rows are grouped, including grouping restored from storage on mount, which MUI's
  // onRowGroupingModelChange never reports. A caller holding one server page can't group it correctly.
  onIsGroupedChange?: (isGrouped: boolean) => void
  // When set on a table using `unstable_dataSource`, this refetches the table data whenever the matching queryKey is
  // invalidated or GC'd. IMPORTANT: because it refetches grid data on GC - ensure that the GC time is suitably high for your use case.
  dataSourceResyncQueryKey?: QueryKey
  // Replaces the default full `fetchRows()` that a `dataSourceResyncQueryKey` hit triggers. Must be stable.
  resyncDataSource?: () => void
}

export const DataGridBase = ({
  sx,
  stateKey,
  loading,
  defaultSortModel,
  initialColumnVisibility,
  defaultColumnVisibilityModel,
  onColumnVisibilityModelChange: onColumnVisibilityModelChangeProp,
  slots,
  getDefaultRowLink,
  allowPageScroll,
  maxHeight,
  pinnedColumns,
  hideFooter,
  apiRef: apiRefProp,
  editMode = 'row',
  TableEmptyState = DefaultNoRowsState,
  onRowClick,
  onFilterModelChange,
  onSortModelChange,
  filterModel,
  pageSizeOptions = [10, 25, 50, 100],
  hasExternalHeader = false,
  totalCountLoading = false,
  totalCountIsApprox = false,
  enableServerSideRowGrouping = false,
  sanitizeRestoredGrouping,
  unpinColumnsWhileGrouped = false,
  rowCount,
  getRowVariant,
  isRowClickable,
  className: classNameProp,
  dataSourceResyncQueryKey,
  resyncDataSource,
  rowGroupingModel: controlledRowGroupingModel,
  onIsGroupedChange,
  getRowSelectionStatus,
  ...props
}: DataGridBaseProps) => {
  const localeText = useMuiDataGridLocaleText()
  const defaultApiRef = useGridApiRef()
  const apiRef = useState(apiRefProp ?? defaultApiRef)[0]
  // MUI copies paginationMode into its state only on mount and never updates it, so a grid switched from paging
  // its own rows to showing a server page keeps slicing client-side (page 2 of a 10-row page shows one row).
  // Keep that copy in step, before paint, for a caller that switches modes. A grid that doesn't pass the prop
  // (e.g. one driven by unstable_dataSource) is left to MUI.
  const paginationMode = props.paginationMode
  useLayoutEffect(() => {
    if (paginationMode === undefined) return
    apiRef.current?.setState?.((state) =>
      state.pagination.paginationMode === paginationMode
        ? state
        : { ...state, pagination: { ...state.pagination, paginationMode } }
    )
  }, [apiRef, paginationMode])
  useSyncDataSourceOnInvalidate(apiRef, dataSourceResyncQueryKey, resyncDataSource)
  const { currentlyEditingRow } = useEditingRowsContext()
  const pathname = usePathname()

  const localStorageKey = isDefined(stateKey) ? getTableStateLocalStorageKey(stateKey) : null
  const initialState = useMemo(() => pinGroupingColumnFirst(getLocalStorageState(localStorageKey)), [localStorageKey])

  // Server-side grouping is single-level only, so keep at most one field — a nested model would send just
  // its first level to the backend while the grid held the full tree (they'd diverge). Keep the last, not the
  // first: MUI appends a newly-selected field, so `slice(-1)` makes switching grouping adopt the newest choice.
  // Keep the input's identity when nothing needs clamping: MUI reads a new array as a model change and refetches.
  const clampServerGrouping = (model: GridRowGroupingModel): GridRowGroupingModel =>
    enableServerSideRowGrouping && model.length > 1 ? model.slice(-1) : model

  const restoredGrouping = clampServerGrouping(initialState.rowGrouping?.model ?? [])
  const sanitizedRestoredGrouping = sanitizeRestoredGrouping ? sanitizeRestoredGrouping(restoredGrouping) : restoredGrouping

  // Suppress grouping on data-source tables unless opted in; when opted in, honour the persisted model.
  const [internalRowGroupingModel, setInternalRowGroupingModel] = useState<GridRowGroupingModel>(
    props.unstable_dataSource && !enableServerSideRowGrouping ? [] : sanitizedRestoredGrouping
  )
  // A caller can pin the grouping (an always-grouped view); then it's fixed, not user-editable.
  const isRowGroupingControlled = controlledRowGroupingModel !== undefined
  const rowGroupingModel = isRowGroupingControlled ? clampServerGrouping(controlledRowGroupingModel) : internalRowGroupingModel
  const isGrouped = rowGroupingModel.length > 0
  useEffect(() => {
    onIsGroupedChange?.(isGrouped)
  }, [isGrouped, onIsGroupedChange])
  const sortModel = useMemo(() => (defaultSortModel ? [defaultSortModel] : undefined), [defaultSortModel])

  const unpinWhileGrouped = unpinColumnsWhileGrouped && rowGroupingModel.length > 0 && isDefined(pinnedColumns)

  // While grouping is active the pinned-left column is folded into the grouping column via `leafField`;
  // hide its standalone column so the value doesn't render twice (a stray duplicate column).
  const pinnedLeftField = pinnedColumns?.left?.[0]
  const groupingLeafField = rowGroupingModel.length > 0 ? pinnedLeftField : undefined

  // A new object identity makes MUI re-hydrate every column, so build it once per input.
  const groupingColDef = useMemo(
    () => ({ ...props.groupingColDef, leafField: pinnedLeftField }),
    [props.groupingColDef, pinnedLeftField]
  )

  const isRowSelectionEnabled = isDefined(getRowSelectionStatus)
  const safeColumns = useMemo(() => {
    const columns = isRowSelectionEnabled ? [ROW_SELECTION_CHECKBOX_COLUMN, ...props.columns] : props.columns
    return columns.map(skipValueGetterOnAutogeneratedRows)
  }, [props.columns, isRowSelectionEnabled])
  const isSelectableByRowSelectionStatus = useCallback(
    ({ row }: GridRowParams) => getRowSelectionStatus === undefined || getRowSelectionStatus(row).isSelectable,
    [getRowSelectionStatus]
  )

  // Order columns based on stored table state
  const orderedColumns = useMemo(() => {
    const tableState = getLocalStorageState(localStorageKey)
    const orderedFields = tableState.columns?.orderedFields
    if (!isArray(orderedFields) || props.disableColumnReorder) {
      return safeColumns
    }

    // NOTE (Dalyan): We need to ensure that the persisted column widths from localStorage
    // are used for the returned `orderingColumns` value - otherwise, it will set the column width
    // to the default value defined by us in the column config, or the MUI default of `100`
    const columnsWithWidth = safeColumns.map((column) => {
      const width = (tableState.columns?.dimensions ?? {})[column.field]?.width
      return { ...column, ...(isDefined(width) && { width }) }
    })

    return mergeColumnOrder(columnsWithWidth, orderedFields)
  }, [safeColumns, localStorageKey, props.disableColumnReorder])

  const handleStateChange = useCallback(
    (state: GridStatePremium) => {
      if (!isDefined(localStorageKey) || gridShowsSkeleton(state)) return

      const exportedState = apiRef?.current?.exportState()
      if (isDefined(exportedState)) {
        const unfoldedState = stripPersistedGroupingLeafFold(exportedState, groupingLeafField)
        // The grid's own pins are emptied while grouping; persist the user's, or reloading grouped would restore none.
        const persistedState = unpinWhileGrouped ? { ...unfoldedState, pinnedColumns } : unfoldedState
        trySaveToLocalStorage({ key: localStorageKey, value: JSON.stringify(persistedState) })
      }
    },
    [apiRef, localStorageKey, groupingLeafField, unpinWhileGrouped, pinnedColumns]
  )

  const hasScrolledHorizontally = useHasScrolledHorizontally(apiRef)
  const hasFilterPanel = isDefined(slots?.filterPanel)

  const { trackRowClick, trackFilterChange, trackSortChange, trackColumnVisibilityChange } = useDataGridAnalytics({
    loading: !!loading,
    rows: Array.from(props.rows ?? []),
    tableName: isDefined(stateKey) ? stateKey : (pathname ?? 'unknown_table'),
    filterModel,
    sortModel,
    initialState,
    rowGroupingModel,
    columnVisibilityModel: props.columnVisibilityModel ?? initialColumnVisibility,
    defaultColumnVisibilityModel,
  })

  // Check if we're using V2 data source pattern
  const isUsingDataSource = !!props.unstable_dataSource

  // Preserve scroll position across re-renders
  usePreserveScrollPosition({
    apiRef,
    isUsingDataSource,
    rows: props.rows ?? [],
    stateKey,
  })

  const handleRowClick = (
    params: GridRowParams,
    event: MuiEvent<React.MouseEvent<HTMLElement>>,
    details: GridCallbackDetails
  ) => {
    if (isRowClickable && !isGroupRow(apiRef, params.id) && !isRowClickable(params.row)) return
    trackRowClick()
    if (onRowClick) {
      onRowClick(params, event, details)
    }
  }

  const handleFilterModelChange = (updatedFilterModel: GridFilterModel, details: GridCallbackDetails) => {
    trackFilterChange(updatedFilterModel, details)
    if (onFilterModelChange) {
      onFilterModelChange(updatedFilterModel, details)
    }
  }

  const handleSortModelChange = (updatedSortModel: GridSortModel, details: GridCallbackDetails) => {
    trackSortChange(updatedSortModel, details)
    if (onSortModelChange) {
      onSortModelChange(updatedSortModel, details)
    }
  }

  // MUI only invokes this for user-initiated toggles, not for the initialState/controlled-prop
  // hydration that restores persisted visibility on mount — so tracking here never fires on restore.
  const handleColumnVisibilityModelChange = (updatedModel: GridColumnVisibilityModel, details: GridCallbackDetails) => {
    trackColumnVisibilityChange(updatedModel)
    onColumnVisibilityModelChangeProp?.(updatedModel, details)
  }

  const paginationSlot = useCallback(
    () => (
      <CustomPagination
        totalCountLoading={totalCountLoading}
        totalCountIsApprox={totalCountIsApprox}
        approxTotalCount={rowCount}
      />
    ),
    [totalCountLoading, totalCountIsApprox, rowCount]
  )

  const emptySlot = useCallback(() => null, [])

  const mergedSlotProps = useMemo(
    () => ({
      ...props.slotProps,
      loadingOverlay: { ...LOADING_OVERLAY_SLOT_PROPS, ...props.slotProps?.loadingOverlay },
      row: { getRowVariant, isRowClickable, ...props.slotProps?.row },
    }),
    [props.slotProps, getRowVariant, isRowClickable]
  )

  const mergedSlots = useMemo(
    () => ({
      noRowsOverlay: isNil(currentlyEditingRow) ? TableEmptyState : emptySlot,
      noResultsOverlay: isNil(currentlyEditingRow) ? TableEmptyState : emptySlot,
      row: LinkableGridRow,
      pagination: paginationSlot,
      ...slots,
    }),
    [currentlyEditingRow, TableEmptyState, emptySlot, paginationSlot, slots]
  )

  const roundTopCorners = !(hasFilterPanel || hasExternalHeader)

  return (
    <DataGridContainer maxHeight={maxHeight}>
      <RowLinkContext.Provider value={getDefaultRowLink}>
        <RowSelectionStatusContext.Provider value={getRowSelectionStatus}>
          <DataGridPremium
            editMode={editMode}
            onProcessRowUpdateError={(error) => console.error('Failed to process row update', { error })}
            pagination
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            {...(!isUsingDataSource && {
              onFilterModelChange: handleFilterModelChange,
              onSortModelChange: handleSortModelChange,
            })}
            loading={loading}
            columnHeaderHeight={40}
            pageSizeOptions={pageSizeOptions}
            filterDebounceMs={500}
            // Modifier classes consumed by the omui MuiDataGrid theme override
            // (see apps/frontend/shared/omui/src/theme2/components/MuiDataGrid.ts)
            // to opt into the rounded-corner rules. classNameProp is merged here
            // because the V1 `{...props}` spread below would otherwise overwrite
            // our modifier classes if a consumer passes className.
            className={clsx(
              roundTopCorners && omneaDataGridClasses.roundTop,
              !roundTopCorners && omneaDataGridClasses.roundPinnedLeftHeaderTop,
              !hasFilterPanel && omneaDataGridClasses.roundHeaderRowTop,
              !hasExternalHeader && omneaDataGridClasses.roundFirstChildTop,
              hideFooter && omneaDataGridClasses.roundBottom,
              allowPageScroll && omneaDataGridClasses.allowPageScroll,
              isDefined(onRowClick) && omneaDataGridClasses.clickableRows,
              classNameProp
            )}
            sx={{
              // Vast majority of the styling here should be part of the Omui theme override, NOT here!
              // Only bits which depend on props
              '& .MuiDataGrid-virtualScroller': {
                overscrollBehavior: allowPageScroll ? 'auto' : 'none', // Prevents scroll overshoot/scroll chaining when allowPageScroll is false; uses default scrolling when true
                // Disable vertical scrolling when table is empty to allow page scroll (preserve horizontal for column headers)
                ...(props.rows && props.rows.length === 0 && { overflowY: 'hidden !important' }),
              },
              ...sx,
              '& .MuiDataGrid-cell--pinnedLeft': {
                boxShadow: hasScrolledHorizontally ? '8px 0px 15px -8px #D1D1D1' : 'none',
              },
            }}
            onStateChange={handleStateChange}
            showColumnVerticalBorder
            localeText={localeText}
            slots={mergedSlots}
            initialState={{
              sorting: {
                sortModel,
              },
              ...initialState,
              ...(pinnedColumns && { pinnedColumns }),
            }}
            columnVisibilityModel={initialColumnVisibility}
            pinnedColumns={unpinWhileGrouped ? NO_PINNED_COLUMNS : rowGroupingModel.length === 0 ? pinnedColumns : undefined}
            groupingColDef={groupingColDef}
            hideFooter={hideFooter}
            {...(isUsingDataSource
              ? {
                  unstable_dataSource: props.unstable_dataSource,
                  slotProps: mergedSlotProps,
                  // The data source owns rowCount (it calls setRowCount from getRows); a controlled rowCount
                  // prop fights that reconciliation and drops the fetched rows once an exact count resolves.
                  disableAggregation: props.disableAggregation,
                  filterModel,
                  onFilterModelChange: handleFilterModelChange,
                  sortModel: props.sortModel,
                  onSortModelChange: handleSortModelChange,
                  processRowUpdate: props.processRowUpdate,
                  paginationModel: props.paginationModel,
                  onPaginationModelChange: props.onPaginationModelChange,
                  columnVisibilityModel: groupingLeafField
                    ? { ...props.columnVisibilityModel, [groupingLeafField]: false }
                    : props.columnVisibilityModel,
                  onColumnVisibilityModelChange: (model, details) => {
                    if (groupingLeafField) {
                      const { [groupingLeafField]: _folded, ...rest } = model
                      handleColumnVisibilityModelChange(rest, details)
                      return
                    }
                    handleColumnVisibilityModelChange(model, details)
                  },
                  unstable_dataSourceCache: null,
                  checkboxSelection: props.checkboxSelection,
                  rowSelectionModel: props.rowSelectionModel,
                  onRowSelectionModelChange: props.onRowSelectionModelChange,
                  keepNonExistentRowsSelected: props.keepNonExistentRowsSelected,
                  getRowHeight: props.getRowHeight,
                  isCellEditable: props.isCellEditable,
                  onCellEditStart: props.onCellEditStart,
                  isGroupExpandedByDefault: props.isGroupExpandedByDefault,
                }
              : {
                  ...props,
                  slotProps: mergedSlotProps,
                  filterModel,
                  ...(rowCount != null && { rowCount: totalCountIsApprox ? -1 : rowCount }),
                })}
            {...(unpinWhileGrouped && { disableColumnPinning: true })}
            // getRowSelectionStatus takes over from isRowSelectable, so a checkbox and its tooltip cannot disagree.
            isRowSelectable={isRowSelectionEnabled ? isSelectableByRowSelectionStatus : props.isRowSelectable}
            apiRef={apiRef}
            columns={orderedColumns}
            rowGroupingModel={rowGroupingModel}
            onRowGroupingModelChange={(model) => {
              if (!isRowGroupingControlled) setInternalRowGroupingModel(clampServerGrouping(model))
            }}
          />
        </RowSelectionStatusContext.Provider>
      </RowLinkContext.Provider>
    </DataGridContainer>
  )
}

function getLocalStorageState(key: string | null): GridInitialState {
  try {
    const stateFromLocalStorage = isDefined(key) ? localStorage.getItem(key) : null
    return isDefined(stateFromLocalStorage) ? JSON.parse(stateFromLocalStorage) : {}
  } catch (error) {
    console.error('Error getting table state from local storage', error)
    return {}
  }
}

export function getTableStateLocalStorageKey(name: string): string {
  return `${name}_TABLE_STATE`
}
